import {
    FlightScheduleAvailabilityStatusEnum,
    IFlightScheduleCache,
    IFlightScheduleDayCache
} from "./flights-schedule.service.interface";
import {ILargeStorageConsumer} from "../storage/large-storage-consumer.interface";
import {IServiceFactory} from "../service-factory.interface";
import {runInAction} from "mobx";
import {Check} from "../../types/type-checking";
import {TimeSpan} from "../../types/time-span";
import {RawSessionStorageKeys} from "../storage/raw-session-storage/raw-session-storage-keys";
import {ILargeCacheData, LargeCacheHandler} from "../storage/large-cache.handler";
import {ILargeCacheHandlerEventSubscription} from "../storage/large-cache.handler.interface";

interface RouteCachedSchedule extends ILargeCacheData {
    //the key is the date
    dates: Record<string, FlightScheduleAvailabilityStatusEnum>;
}

const CACHE_EXPIRATION_TIME = TimeSpan.fromMinutes(30);

export class FlightScheduleCache implements IFlightScheduleCache, ILargeStorageConsumer {
    constructor(private readonly services: IServiceFactory) {

        //the key of the cache is the route (origin-destination)
        this._largeCacheHandler = new LargeCacheHandler<RouteCachedSchedule, RawSessionStorageKeys>({
            name: "flight schedule",
            services: this.services,
            maxItems: 20,
            cacheExpirationTime: CACHE_EXPIRATION_TIME,
            resetOnAuthorizationChanged: false,
            storage: this.services.rawSessionStorage,
            storageKey: RawSessionStorageKeys.FlightSearchScheduleCache
        })

        this.services.largeStorageConsumersManager.registerSessionStorageConsumer(this);

    }


    private readonly _largeCacheHandler: LargeCacheHandler<RouteCachedSchedule, RawSessionStorageKeys>;

    clearUsedStorage(): void {
        this._largeCacheHandler.resetCache();
    }

    onRouteRemovedFromCache(callback: (key: string) => void): ILargeCacheHandlerEventSubscription {
        return this._largeCacheHandler.onCacheItemRemoved(callback);
    }

    private _getFromCache(origin: string, destination: string): RouteCachedSchedule | undefined {
        return this._largeCacheHandler.getItem(this._computeRouteKey(origin, destination));
    }

    isCacheExpired(origin: string, destination: string, date: string): boolean {
        const schedule = this._getFromCache(origin, destination);
        if(!schedule) {
            return true;
        }

        if(this._largeCacheHandler.isTimestampExpired(schedule.timestamp)) {
            return true;
        }

        return Check.isNullOrUndefined(schedule.dates[date]);
    }

    getFromCache(origin: string, destination: string, date: string): IFlightScheduleDayCache | null {
        const schedule = this._getFromCache(origin, destination);
        if(!schedule) {
            return null;
        }

        return {
            dayScheduleStatus: schedule.dates[date] ?? null,
            isExpired: this._largeCacheHandler.isTimestampExpired(schedule.timestamp)
        }
    }


    putInCache(origin: string, destination: string, date: string, status: FlightScheduleAvailabilityStatusEnum): void {
        const routeKey = this._computeRouteKey(origin, destination);
        const schedule = this._largeCacheHandler.getItem(routeKey);

        runInAction(() => {
            if(schedule) {
                schedule.timestamp = Date.now();
                schedule.dates[date] = status;
                this._largeCacheHandler.saveCache({saveAsync: true});
            } else {
                this._largeCacheHandler.setItem(routeKey, {
                    timestamp: Date.now(),
                    dates: {
                        [date]: status
                    }
                })
            }
        });
    }

    setExpirationTime(origin: string, destination: string, timeSpanFromNow: TimeSpan): void {
        const schedule = this._getFromCache(origin, destination);
        if(!schedule) {
            return;
        }

        runInAction(() => {
            schedule.timestamp = Date.now() - CACHE_EXPIRATION_TIME.totalMilliseconds + timeSpanFromNow.totalMilliseconds;
        });
    }

    private _computeRouteKey(origin: string, destination: string): string {
        return `${origin}-${destination}`;
    }


}